// src/lib/leg-stats-link.js -- v1c slip-leg -> Stats navigation intent.
//
// Single job: turn a player-prop slip leg descriptor into a Stats-tab navigation
// intent -- either a { kind:'player' } deep-link (resolved player + focus stat +
// highlighted game) or a { kind:'search' } fallback (pre-filled search). It owns the
// market->focus-stat mapping and the one server call to the /api/player-stats resolve
// mode. It does NOT navigate, hold state, or render (App.js consumes the intent via
// useStatsNavigation). Fail-soft: any resolve failure degrades to the search fallback
// so the deep-link never hard-errors the slip view. Spec v1c-addendum §E.

import { captureMessage } from './errorReporting';
import { apiFetch } from './api';
import { STAT_DEFINITIONS } from './player-stats-display';

// Canonical/legacy market key -> STAT_DEFINITIONS focus id. Keep in sync with
// markets.js and STAT_DEFINITIONS (CC-IMPL-027 pairing applies to any additions).
// Both namespaced (`nhl:shots_on_goal`) and legacy display keys (`sog`, `anytime_goal`)
// are covered because a leg's marketKey may be either. Binary props map to their
// underlying counting stat (anytime_goal -> goals). An unmapped market -> null focus,
// which opens the player page unfocused (valid per the nav contract).
const MARKET_TO_FOCUS = {
  // NHL skater
  'nhl:shots_on_goal': 'nhl:sog',
  sog: 'nhl:sog',
  'nhl:goals': 'nhl:goals',
  goals: 'nhl:goals',
  anytime_goal: 'nhl:goals',
  'nhl:anytime_goal': 'nhl:goals',
  'nhl:assists': 'nhl:assists',
  assists: 'nhl:assists',
  anytime_assist: 'nhl:assists',
  'nhl:anytime_assist': 'nhl:assists',
  'nhl:points': 'nhl:points',
  points: 'nhl:points',
  anytime_point: 'nhl:points',
  'nhl:anytime_point': 'nhl:points',
  'nhl:blocks': 'nhl:blocks',
  blocks: 'nhl:blocks',
  'nhl:hits': 'nhl:hits',
  'nhl:pim': 'nhl:pim',
  pim: 'nhl:pim',
  // NHL goalie
  'nhl:saves': 'nhl:saves',
  saves: 'nhl:saves',
  // MLB (namespaced only -- no legacy collisions with NHL bare keys)
  'mlb:hits': 'mlb:hits',
  'mlb:home_runs': 'mlb:hr',
  'mlb:rbi': 'mlb:rbi',
  'mlb:rbis': 'mlb:rbi',
  'mlb:runs': 'mlb:runs',
  'mlb:total_bases': 'mlb:tb',
  'mlb:batter_strikeouts': 'mlb:bk',
  'mlb:pitcher_strikeouts': 'mlb:pk',
  'mlb:earned_runs': 'mlb:er',
  'mlb:pitcher_hits_allowed': 'mlb:ph',
  // NFL (#1080; namespaced only). The four settleable markets map to chips; nfl:anytime_td
  // has no single chip (rushing + receiving TDs) and stays unmapped -> unfocused page.
  'nfl:passing_yards': 'nfl:pass_yds',
  'nfl:rushing_yards': 'nfl:rush_yds',
  'nfl:receiving_yards': 'nfl:rec_yds',
  'nfl:receptions': 'nfl:rec',
};

// Leagues whose game log the Stats route serves (mirrors api/lib/player-stats-query.js
// STATS_LEAGUES -- the client cannot import from api/; leg-stats-link.test.js pins the two
// equal). A leg outside this set fails soft to search rather than building a deep-link
// the route would 400.
export const DEEP_LINK_LEAGUES = new Set(['nhl', 'mlb', 'nfl']);

// Only hit-rate-eligible, non-context-only stats are valid focus targets -- those are
// exactly the chips StatsPage renders, so focusing a context-only stat (e.g. save %,
// ERA) would land on a stat with no chip. Building the set from eligible definitions
// (not mere existence) fails a future mis-mapping closed to a null focus (GPT pre-audit
// P2#3).
const FOCUSABLE_STAT_IDS = new Set(
  STAT_DEFINITIONS.filter((d) => d.hitRateEligible === true && d.contextOnly !== true).map((d) => d.id)
);

/**
 * Map a canonical (or legacy) market key to a STAT_DEFINITIONS focus id, or null when
 * unmapped / non-prop. Pure. The result is validated against STAT_DEFINITIONS so a
 * stale table entry degrades to null rather than a bad focus id. `league` is accepted
 * for future disambiguation of bare keys; current tables are unambiguous without it.
 */
// Is this market a deep-linkable player-prop market? Membership in MARKET_TO_FOCUS is
// the purpose-built allowlist for the tap gate -- do NOT rely on markets.js
// isPlayerPropMarket() alone, which classifies genuine player-identity markets
// (nhl:goals / goals) as playerProp:false (documented in
// docs/specs/issue-769-synced-leg-enrichment-fix.md). Callers OR this with
// isPlayerPropMarket so either signal admits a leg. Prototype-safe.
export function isDeepLinkableMarket(marketKey) {
  return typeof marketKey === 'string'
    && Object.prototype.hasOwnProperty.call(MARKET_TO_FOCUS, marketKey);
}

export function marketToFocusStat(marketKey, league) { // eslint-disable-line no-unused-vars
  if (typeof marketKey !== 'string' || !marketKey) return null;
  // hasOwnProperty guard so a marketKey of __proto__/constructor/etc. can't return an
  // inherited prototype value (defense-in-depth; the FOCUSABLE allow-list already
  // rejects such values, but a future refactor of that check would expose it).
  const focus = Object.prototype.hasOwnProperty.call(MARKET_TO_FOCUS, marketKey)
    ? MARKET_TO_FOCUS[marketKey] : null;
  return focus && FOCUSABLE_STAT_IDS.has(focus) ? focus : null;
}

/**
 * Resolve a player-prop leg descriptor to a Stats navigation intent. Descriptor:
 * { name, gameId, marketKey, league }. Returns:
 *   { kind:'player', playerId, league, focusStat, gameId }  -- confident single in-game match
 *   { kind:'search', query }                                -- fallback (unresolved/uncollected/error)
 * Never throws: a missing name/game/league, a null resolution, or any request failure all
 * degrade to the search fallback (fail-soft, spec §E). Recoverable failures are logged
 * at warning (CC-IMPL-037).
 */
export async function resolveLegToStatsNav(descriptor, { fetchImpl = apiFetch } = {}) {
  const name = descriptor?.name || '';
  const gameId = descriptor?.gameId || null;
  // The Stats game log is league-qualified (Deliverable B, spec §7): per-league player IDs are
  // independent sequences that can collide, so a player deep-link carries the tapped leg's league
  // through to StatsPage's loadGameLog. Only a Stats-served league produces a deep-link (#1080
  // admitted nfl); an absent/unrecognized league fails soft to search rather than guessing.
  const league = typeof descriptor?.league === 'string' && DEEP_LINK_LEAGUES.has(descriptor.league) ? descriptor.league : null;
  // Pass the SANITIZED league (never the raw descriptor value) into the focus mapper -- the param
  // is currently unused there, but this keeps raw sportsbook-derived data out of the helper.
  const focusStat = marketToFocusStat(descriptor?.marketKey, league);
  const fallback = { kind: 'search', query: name };

  // Cannot resolve a player deep-link without a name, a game, AND a valid league -- never guess.
  if (!name || !gameId || !league) return fallback;

  try {
    const res = await fetchImpl(
      `/api/player-stats?name=${encodeURIComponent(name)}&gameId=${encodeURIComponent(gameId)}`
    );
    if (!res.ok) {
      captureMessage('[stats] leg resolve request failed (search fallback)', {
        level: 'warning', tags: { function: 'leg-stats-link', stage: 'resolve' }, extra: { status: res.status },
      });
      return fallback;
    }
    const data = await res.json();
    // Re-validate the endpoint's playerId shape before navigating on it -- a server
    // regression / schema drift returning a non-positive-int (0, -5, "abc", bool,
    // float) must fail SOFT to search, not deep-link into a broken player page. Mirrors
    // the server-side isValidPlayerId guard searchPlayers() applies for the same reason
    // (pre-Codex Fail-Closed P2#1).
    const pid = data && data.playerId;
    const asNum = Number(pid);
    // Positive integer within JS safe-integer range (upper bound mirrors the server's
    // isValidPlayerId cap): an out-of-range id would be a precision-mangled, non-existent
    // player, so it fails soft to search rather than deep-linking into an empty page.
    const validPid =
      (typeof pid === 'number' || (typeof pid === 'string' && /^\d+$/.test(pid))) &&
      Number.isInteger(asNum) && asNum > 0 && asNum <= Number.MAX_SAFE_INTEGER;
    if (validPid) {
      return { kind: 'player', playerId: asNum, league, focusStat, gameId };
    }
    return fallback;
  } catch (err) {
    // Network/parse throw recovers to the search fallback -> warning, not exception
    // (CC-IMPL-037). The slip view must never hard-error on a deep-link.
    captureMessage('[stats] leg resolve threw (search fallback)', {
      level: 'warning', tags: { function: 'leg-stats-link', stage: 'resolve' }, extra: { message: err.message },
    });
    return fallback;
  }
}
